<template>
  <div>
    <div class="text-center text-primary text-weight-bold" style="font-size: 2.75rem">
      Real-time data & analytics for your portfolio
    </div>
    <div class="text-center text-grey-7 q-mb-xl text-subtitle1">
      Leverage daily data and insights from more than 5 million multifamily properties and gain an
      edge on your competition
    </div>
    <div class="q-gutter-y-xl q-mt-xl">
      <div class="row justify-center q-gutter-md">
        <q-spinner v-if="trialConsumed === null" class="text-h4 text-center full-width" />
        <template v-else>
          <dashboard-card
            icon="code"
            title="API"
            color="primary"
            href="https://www.hellodata.ai/multifamily-market-analysis-demo"
            action-label="Schedule Demo"
            style="max-width: 416px; width: 100%"
          >
            <div class="q-mb-xl" style="height: 474px">
              <div class="row">
                <div class="text-h3">$0.50</div>
                <div class="q-mt-auto text-subtitle1">/call</div>
              </div>
              <div class="q-mb-sm text-subtitle2">Best to power your own software</div>
              <div class="q-mt-md" style="height: 84px">
                Include the best multifamily data right into your products, models, and existing
                processes. Take a look at our
                <a href="https://docs.hellodata.ai" class="text-primary" target="_blank"
                  >documentation</a
                >
                and see exactly what you'll get after contacting us.
              </div>
              <q-separator class="q-my-md" />
              <div class="q-my-lg q-gutter-xs">
                <div v-for="(f, index) in apiFeatures" :key="index">
                  <span @mouseover="mouseover(f.label)" @mouseleave="mouseleave(f.label)">
                    <q-icon name="check" class="q-mr-sm" />
                    <span>{{ f.label }}</span>
                    <q-icon name="info_outline" class="q-ml-xs" />
                  </span>
                  <q-menu :ref="f.label" style="width: 250px" transition-duration="100">
                    <q-banner> {{ f.extra }} </q-banner>
                  </q-menu>
                </div>
              </div>
            </div>
          </dashboard-card>
          <dashboard-card
            icon="bolt"
            title="Professional"
            color="accent"
            :to="{ name: 'Subscribe to Property Reports' }"
            :action-label="trialConsumed ? 'Resume Subscription' : '7-Day Free Trial'"
            style="max-width: 416px; width: 100%"
          >
            <div class="q-mb-xl" style="height: 474px">
              <div class="row">
                <div class="text-h3">$300</div>
                <div class="q-mt-auto text-subtitle1">/mo</div>
              </div>
              <div class="q-mb-sm text-subtitle2">Best for individuals or small teams</div>
              <div class="q-mt-md" style="height: 84px">
                Power your rent surveys with
                <b>real-time data on over 35M multifamily units</b> nationwide, sourced entirely
                from property websites and public data sources.
              </div>
              <q-separator class="q-my-md" />
              <div class="q-my-lg q-gutter-xs">
                <div v-for="(f, index) in features" :key="index">
                  <span @mouseover="mouseover(f.label)" @mouseleave="mouseleave(f.label)">
                    <q-icon name="check" class="q-mr-sm" />
                    <span>{{ f.label }}</span>
                    <q-icon name="info_outline" class="q-ml-xs" />
                  </span>
                  <q-menu :ref="String(f.label)" style="width: 250px" transition-duration="100">
                    <q-banner> {{ f.extra }} </q-banner>
                  </q-menu>
                </div>
              </div>
              <q-table
                dense
                :rows="rows"
                :columns="[
                  {
                    field: 'name',
                    name: 'name',
                    label: 'Name',
                    sortable: true,
                    align: 'left'
                  }
                ]"
                flat
                bordered
                hide-bottom
                hide-header
              >
                <template #body="props">
                  <q-tr :props="props" @dblclick="$router.push({ name: props.row.name })">
                    <q-td v-for="(col, colIndex) in props.cols" :key="col.name" :props="props">
                      <router-link
                        v-if="colIndex === 0"
                        :to="{ name: props.row.name }"
                        class="text-primary"
                      >
                        {{ col.value }}
                      </router-link>
                      <template v-else>
                        {{ col.value }}
                      </template>
                    </q-td>
                    <q-td style="text-align: right">
                      <q-btn
                        :id="`intercom-launch-report-${props.rowIndex}`"
                        no-caps
                        unelevated
                        flat
                        color="primary"
                        :to="{ name: props.row.name }"
                      >
                        Try it out!
                      </q-btn>
                    </q-td>
                  </q-tr>
                </template>
              </q-table>
            </div>
          </dashboard-card>
          <dashboard-card
            icon="business"
            title="Enterprise"
            color="primary"
            href="https://www.hellodata.ai/multifamily-market-analysis-demo"
            action-label="Schedule Demo"
            style="max-width: 416px; width: 100%"
          >
            <div class="q-mb-xl" style="height: 474px">
              <div class="row">
                <div class="text-h3">Custom</div>
              </div>
              <div class="q-mb-sm text-subtitle2">Best for national rollouts</div>
              <div class="q-mt-md" style="height: 84px">
                Benefit from bulk discounts for large teams. Easily integrate your SSO and get
                access to our rent surveys and APIs without limits. No long and costly onboarding,
                start diving in right away.
              </div>
              <q-separator class="q-my-md" />
              <div class="q-my-lg q-gutter-xs">
                <div v-for="(f, index) in enterpriseFeatures" :key="index">
                  <span @mouseover="mouseover(f.label)" @mouseleave="mouseleave(f.label)">
                    <q-icon name="check" class="q-mr-sm" />
                    <span>{{ f.label }}</span>
                    <q-icon name="info_outline" class="q-ml-xs" />
                  </span>
                  <q-menu :ref="f.label" style="width: 250px" transition-duration="100">
                    <q-banner> {{ f.extra }} </q-banner>
                  </q-menu>
                </div>
              </div>
            </div>
          </dashboard-card>
        </template>
      </div>
      <div>
        <div class="text-center text-primary text-weight-medium">
          Our Data is Your Competitive Advantage.
        </div>
        <div class="text-h5 text-center q-mb-lg text-weight-medium">
          The New Gold Standard in Multifamily Data
        </div>
        <div class="row q-col-gutter-md">
          <div v-for="(q, index) in quotes" :key="index" class="col-md-4 col-12 row">
            <quote-card :quote="q.text" :name="q.name" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import DashboardCard from '@/components/DashboardCard.vue'
import QuoteCard from '@/components/QuoteCard.vue'
import { propertyReportSamples } from 'common'
import { useStripeStore } from '@/stores/stripe'
import type { QPopupProxy } from 'quasar'

export default {
  name: 'PropertyReportsNotSubscribed',
  components: { DashboardCard, QuoteCard },
  data() {
    return {
      rows: propertyReportSamples,
      apiFeatures: [
        {
          label: 'Rent Comps API',
          extra:
            'Identify the most statistically relevant rent comps in any market using computer vision and AI.'
        },
        {
          label: 'Property Data API',
          extra:
            'Get daily updates on rent, availability, concessions and time on market for more than 5 million multifamily properties across the U.S.'
        },
        {
          label: 'Revenue Management API',
          extra:
            'Optimize pricing strategies across property portfolios by leveraging exclusively public data.'
        },
        {
          label: 'Financial Analysis API',
          extra:
            'Benchmark operating expenses against real financial data from over 25k multifamily properties.'
        }
      ],
      features: [
        {
          label: '100% Public Data',
          extra: 'Every data point included is collected from public sources.'
        },
        {
          label: 'Unlimited Reports',
          extra:
            'No limits on the number of properties you can analyze. Scale your real estate portfolio without constraints.'
        },
        {
          label: 'Automatic Rent Comp Analysis',
          extra:
            'Automatically survey and analyze your nearest competitors based on year built, number of units, and quality, to provide a comparative view of asking rents and pricing strategies.'
        },
        {
          label: 'Specials & Effective Rent Extraction',
          extra:
            'Monitor competitor specials to gauge effective rents and fine-tune your pricing strategy accordingly.'
        },
        {
          label: 'Rent Pricing Recommendations',
          extra:
            'Receive unit-level pricing suggestions derived exclusively from publicly available data sources.'
        },
        {
          label: 'Financial Analysis',
          extra:
            "Our estimate of the subject property's stabilized income and operating expenses over the next year, based on operating metrics from similar properties."
        },
        {
          label: 'Regional Reports ($100/mo add-on)',
          extra:
            'Analyze rent and availability trends within your custom market boundaries. Included for free during the trial, and available as an optional add-on for $100/month after the trial ends.'
        }
      ],
      enterpriseFeatures: [
        {
          label: 'Unlimited Rent Survey Seats',
          extra:
            'Enjoy the freedom of adding as many users as needed to your organization without any restrictions.'
        },
        {
          label: 'Unlimited API Usage',
          extra:
            'Access our APIs without limitations on the number of requests, ensuring seamless integration and data access for your applications.'
        },
        {
          label: 'Bulk Data Downloads',
          extra: 'Download datasets in bulk, enabling your team to perform large-scale analyses.'
        },
        {
          label: 'SSO Integration',
          extra:
            'Integrate your entire organization with SAML Single Sign-On preventing yet another username and password.'
        }
      ],
      quotes: [
        {
          text: 'Adding HelloData to our workflows has significantly improved the sophistication of our competitive analytics. The tool is easy to integrate with our existing spreadsheets, making for a seamless addition to our workflow.',
          name: 'Robert B.'
        },
        {
          text: "This platform provides excellent quality analytical insights into competitive intelligence and revenue management. It's easy to use and integrate with existing workflows, and the team is extremely supportive. Highly recommend!",
          name: 'Steven J.'
        },
        {
          text: "HelloData's APIs are well-documented and very easy to use. I was impressed with the data quality and the team's responsiveness during the integration.",
          name: 'Nicholas K.'
        },
        {
          text: 'This software provides data on rent concessions and rent trends for previous months at multifamily properties. This helps increase the quality of a rent comp analysis.',
          name: 'Eric J.'
        },
        {
          text: "This product makes it so easy to find relevant multifamily rent comps. If you're looking to save time copying and pasting from property websites, I'd highly recommend checking it out.",
          name: 'Roswald W.'
        },
        {
          text: "Can't beat HelloData.ai. Best data in the industry and the platform is extremely easy to navigate. Highly recommend!",
          name: 'Evan S.'
        }
      ]
    }
  },
  computed: {
    trialConsumed() {
      return useStripeStore().propertyReportTrialConsumed
    }
  },
  methods: {
    mouseover(label: string) {
      const el = this.$refs[label] as QPopupProxy[]
      if (el) {
        el[0].show()
      }
    },
    mouseleave(label: string) {
      const el = this.$refs[label] as QPopupProxy[]
      if (el) {
        el[0].hide()
      }
    }
  }
}
</script>
